import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { Scene } from 'three';

import { ComputeFunction, createPortal, Size, useFrame, useThree } from '@react-three/fiber';

interface RenderProps {
  children: React.ReactNode;
  scene?: Scene;
  index: number;
  size: Size;
  ratio: number;
}

const Render: React.FC<RenderProps> = ({ children, index, size, ratio }) => {
  useFrame(({ gl, camera, scene }) => {
    const oldScissorTest = gl.getScissorTest();
    if (index === 1) {
      gl.setScissor(0, 0, size.width * ratio, size.height);
    } else {
      gl.setScissor(size.width * ratio, 0, size.width, size.height);
    }
    gl.setScissorTest(true);
    gl.render(scene, camera);
    gl.setScissorTest(oldScissorTest);
  }, index);

  return <>{children}</>;
};
export type ComparisonViewImplProps = {
  index?: number;
  /** The scene to render, if you leave this undefined it will render the default scene */
  children?: React.ReactNode;
  /** The ratio of the two views. Defaults to 0.5. **/
  ratio?: number;
};

export const ComparisonViewImpl = ({
  index = 1,
  ratio = 0.5,
  children,
}: ComparisonViewImplProps) => {
  const { scene, size } = useThree();
  const [virtualScene] = useState(() => new Scene());

  const viewSize = useMemo<Size>(
    () => ({
      width: size.width * ratio,
      height: size.height,
      top: 0,
      left: index === 0 ? 0 : size.width * (index - ratio),
    }),
    [index, ratio, size.height, size.width]
  );

  const compute = useCallback<ComputeFunction>(
    (event, state) => {
      const { width, height, left, top } = viewSize;
      const x = event.clientX - left;
      const y = event.clientY - top;
      state.pointer.set((x / width) * 2 - 1, -(y / height) * 2 + 1);
      state.raycaster.setFromCamera(state.pointer, state.camera);
    },
    [viewSize]
  );

  return useMemo(
    () =>
      createPortal(
        <Render index={index} ratio={ratio} scene={scene} size={size}>
          {children}
          {/* Without an element that receives pointer events state.pointer will always be 0/0 */}
          <group onPointerOver={() => null} />
        </Render>,
        virtualScene,
        {
          events: { compute, priority: index },
          size: viewSize,
        }
      ),
    [children, compute, index, ratio, scene, size, viewSize, virtualScene]
  );
};

export interface ComparisonViewProps {
  children?: React.ReactNode;
}

/**
 * View to compare.
 */
export const ComparisonView: React.FC<ComparisonViewProps> = () => {
  useLayoutEffect(() => {
    throw new Error('ComparisonView you must wrap this component in a <Comparison> component');
  });

  return null;
};

export default ComparisonView;
